import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import screenfull from 'screenfull';
import { UntypedFormControl, Validators, UntypedFormGroup } from '@angular/forms';
import { Settings } from '../model/settings';
import { SettingsService } from '../service/settings.service';
import { Region } from '../model/region';
import { VERSION } from 'src/environments/version';
import { TranslateService } from '@ngx-translate/core';
import NoSleep from 'nosleep.js';
import { EventsService } from '../service/events.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.css']
})
export class SettingsComponent implements OnInit {
  @Output() showSettings = new EventEmitter();

  paramsForm: UntypedFormGroup;
  settings: Settings;
  noSleep = new NoSleep();
  wakeLockEnabled = screenfull.isFullscreen;
  public appVersion = VERSION.raw;
  public cacheBuster = '?cache_buster=' + Date.now();
  public tableLocations = Settings.tableLocations;
  public colorPallet = Settings.colorPalets;
  public baseMapEffects = Settings.baseMapEffects;
  public baseMaps = Settings.baseMaps;
  public languages = Settings.languages;
  public sources = Settings.sources;
  constructor(
    public ss: SettingsService,
    private translateService: TranslateService,
    private eventService: EventsService
  ) { }

  ngOnInit() {
    this.initiateParamForm();
    screenfull.onchange(event => {
      if (!screenfull.isFullscreen) {
        this.noSleep.disable();
      }
    })

    var toggleEl = document.querySelector("#toggle_fs");
    toggleEl.addEventListener('click', function () {
      if (!this.wakeLockEnabled) {
        this.noSleep.enable(); // keep the screen on!
        this.wakeLockEnabled = true;
      } else {
        this.noSleep.disable(); // let the screen turn off.
        this.wakeLockEnabled = false;
      }
    }, false);
  }

  public closeSettings(): void {
    this.showSettings.emit(false);
  }

  public checkAll(event, key: string): void {
    const regions = Object.keys(this.paramsForm.value.regions);
    switch (key) {
      case 'hideRegion':
        regions.forEach(region => this.paramsForm.get('regions').get(region).get('hideRegion').setValue(event.target.checked));
        break;
      case 'minMagnitudeToShow':
        if (+event.target.value >= 3 && +event.target.value <= 10) {
          regions.forEach(region => this.paramsForm.get('regions').get(region).get('minMagnitudeToShow').setValue(event.target.value));
        } else {
          alert('Min magnitude to show needs to be between 3 and 10');
          event.target.value = 3;
        }

        break;
      case 'slideDuration':
        if (+event.target.value >= 10 && +event.target.value <= 600) {
          regions.forEach(region => this.paramsForm.get('regions').get(region).get('slideDuration').setValue(event.target.value));
          break;
        } else {
          alert('Duration needs to be between 10 and 600 seconds');
          event.target.value = 30;
        }
    }
  }

  private crateRegionFormGroup(regions: Region[]): UntypedFormGroup {
    const regionsFormGroup = regions.reduce((acc, region: Region, index: number): any => {
      acc['region-' + index] = new UntypedFormGroup({
        minMagnitudeToShow: new UntypedFormControl(region.minMagnitudeToShow, [Validators.min(3), Validators.max(8)]),
        hideRegion: new UntypedFormControl(region.hideRegion),
        slideDuration: new UntypedFormControl(region.slideDuration / 1000, [Validators.min(10)])
      });
      return acc;
    }, {});
    return new UntypedFormGroup(regionsFormGroup);
  }

  private initiateParamForm(): void {
    this.paramsForm = new UntypedFormGroup({
      dataFetchInterval: new UntypedFormControl(this.ss.settings.dataFetchInterval / 1000, [Validators.min(3 * 60), Validators.max(60 * 60)]),
      tablePosition: new UntypedFormControl(this.ss.settings.tablePosition),
      colorPalletIndex: new UntypedFormControl(this.ss.settings.colorPalletIndex),
      baseMapEffect: new UntypedFormControl(this.ss.settings.baseMapEffect),
      baseMap: new UntypedFormControl(this.ss.settings.baseMap),
      language: new UntypedFormControl(this.ss.settings.language),
      source: new UntypedFormControl(this.ss.settings.source),
      dateType: new UntypedFormControl(this.ss.settings.dateType),
      animateHowMany: new UntypedFormControl(this.ss.settings.animateHowMany),
      cycleTablePosition: new UntypedFormControl(this.ss.settings.cycleTablePosition),
      smartRegion: new UntypedFormControl(this.ss.settings.smartRegion),
      regions: this.crateRegionFormGroup(this.ss.settings.regions)
    });
  }

  public async onSubmit(data): Promise<void> {
    let shouldReload = false;
    if (
      this.ss.settings.colorPalletIndex !== data.colorPalletIndex ||
      this.ss.settings.baseMapEffect !== data.baseMapEffect ||
      this.ss.settings.baseMap !== data.baseMap ||
      this.ss.settings.language !== data.language ||
      this.ss.settings.source !== data.source
    ) {
      shouldReload = true;
    }
    if (this.ss.settings.source !== data.source) {
      this.eventService.clearStored();
    }
    this.ss.settings.dataFetchInterval = data.dataFetchInterval * 1000;
    this.ss.settings.tablePosition = data.tablePosition;
    this.ss.settings.smartRegion = data.smartRegion;
    this.ss.settings.dateType = data.dateType;
    if (this.ss.settings.language !== data.language) {
      this.translateService.use(data.language).subscribe(() => { });
    }
    this.ss.settings.language = data.language;
    this.ss.settings.source = data.source;

    this.ss.settings.colorPalletIndex = data.colorPalletIndex;
    this.ss.settings.cycleTablePosition = data.cycleTablePosition;
    this.ss.settings.animateHowMany = data.animateHowMany;
    this.ss.settings.baseMapEffect = data.baseMapEffect;
    this.ss.settings.baseMap = data.baseMap;
    this.ss.settings.regions.forEach((region: Region, index: number) => {
      region.hideRegion = data.regions['region-' + index].hideRegion;
      region.minMagnitudeToShow = data.regions['region-' + index].minMagnitudeToShow;
      region.slideDuration = data.regions['region-' + index].slideDuration * 1000;
    });
    await this.ss.save();
    if (shouldReload) {
      window.location.reload();
    }
    this.closeSettings();
  }

  public toggleFullScreen(): void {
    if (screenfull && screenfull.isEnabled) {
      screenfull.toggle();
    }
  }

}
